"use client";

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button, Space, Spin, Typography, Card } from "antd";
import { LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined, DownloadOutlined } from "@ant-design/icons";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure PDF.js worker - use CDN for compatibility with react-pdf@9.1.1
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs`;

const { Text } = Typography;

interface PDFViewerProps {
  file: string | File | ArrayBuffer;
  title?: string;
  width?: number;
  height?: number;
  showControls?: boolean;
  showDownload?: boolean;
  className?: string;
}

export type { PDFViewerProps };

const PDFViewer: React.FC<PDFViewerProps> = ({
  file,
  title,
  width = 800,
  height,
  showControls = true,
  showDownload = true,
  className = "",
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [containerWidth, setContainerWidth] = useState<number>(width);
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {

    // Reset loading state
    setError("");
    setLoading(true);
    setNumPages(0);
    
    // Set a timeout for loading
    const timeout = setTimeout(() => {
      console.warn("PDFViewer: Loading timeout - no response after 15 seconds");
      setError("PDF loading timed out. Please check if the PDF file exists and is accessible.");
      setLoading(false);
    }, 15000); // 15 second timeout
    
    setLoadingTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [file]); // Remove 'loading' from dependency array to prevent infinite loop

  // Clear timeout when loading completes
  useEffect(() => {
    if (!loading && loadingTimeout) {
      clearTimeout(loadingTimeout);
      setLoadingTimeout(null);
    }
  }, [loading, loadingTimeout]);

  // Handle responsive width
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        setContainerWidth(screenWidth - 40); // Mobile padding
      } else if (screenWidth < 1024) {
        setContainerWidth(screenWidth - 80); // Tablet padding
      } else {
        setContainerWidth(width);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError("");
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
      setLoadingTimeout(null);
    }
  }, [loadingTimeout]);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error("PDF load error:", error);
    console.error("PDF file path:", file);
    setError(`Failed to load PDF: ${error.message}`);
    setLoading(false);
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
      setLoadingTimeout(null);
    }
  }, [file, loadingTimeout]);

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  }, [numPages]);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  }, []);

  const downloadPDF = useCallback(() => {
    if (typeof file === "string") {
      const link = document.createElement("a");
      link.href = file;
      link.download = title || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [file, title]);

  const pageWidth = containerWidth * scale;
  const pageHeight = height ? height * scale : undefined;

  // Memoize options to prevent unnecessary reloads
  const documentOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  return (
    <div className={`pdf-viewer-container ${className}`}>
      {title && (
        <div className="pdf-viewer-header">
          <Typography.Title level={4} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
        </div>
      )}

      {showControls && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Button
              icon={<LeftOutlined />}
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              size="small"
            >
              Previous
            </Button>
            
            <Text>
              Page {pageNumber} of {numPages}
            </Text>
            
            <Button
              icon={<RightOutlined />}
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              size="small"
            >
              Next
            </Button>
            
            <Button
              icon={<ZoomOutOutlined />}
              onClick={zoomOut}
              disabled={scale <= 0.5}
              size="small"
            >
              Zoom Out
            </Button>
            
            <Text>{Math.round(scale * 100)}%</Text>
            
            <Button
              icon={<ZoomInOutlined />}
              onClick={zoomIn}
              disabled={scale >= 3.0}
              size="small"
            >
              Zoom In
            </Button>
            
            {showDownload && typeof file === "string" && (
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadPDF}
                type="primary"
                size="small"
              >
                Download
              </Button>
            )}
          </Space>
        </Card>
      )}

      <div className="pdf-viewer-content">
        {/* {loading && (
          <div className="pdf-viewer-loading">
            <Spin size="large" />
            <Text style={{ marginTop: 16, display: "block" }}>Loading PDF...</Text>
          </div>
        )} */}

        {error && (
          <div className="pdf-viewer-error">
            <Text type="danger">{error}</Text>
            <Button 
              style={{ marginTop: 16 }}
              onClick={() => {
                setError("");
                setLoading(true);
                setNumPages(0);
                // Force re-render by updating a state
                setPageNumber(1);
              }}
            >
              Retry Loading PDF
            </Button>
          </div>
        )}

        <div 
          className="pdf-document-container"
          style={{ display: error ? 'none' : 'block' }}
        >
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            onSourceSuccess={() => {
              
            }}
            onSourceError={(error) => {
              console.error("PDFViewer: Source load error:", error);
              setError(`Failed to load PDF source: ${error.message}`);
              setLoading(false);
            }}
            options={documentOptions}
            loading={
              <div className="pdf-viewer-loading">
                <Spin size="large" />
                <Text style={{ marginTop: 16, display: "block" }}>Loading PDF...</Text>
              </div>
            }
            error={
              <div className="pdf-viewer-error">
                <Text type="danger">Failed to load PDF document</Text>
                <Button 
                  style={{ marginTop: 16 }}
                  onClick={() => {
                    setError("Please try refreshing the page");
                    setLoading(false);
                  }}
                >
                  Report Issue
                </Button>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              height={pageHeight}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              loading={<Spin />}
              error={
                <div className="pdf-viewer-error">
                  <Text type="danger">Failed to render page</Text>
                </div>
              }
            />
          </Document>
        </div>
      </div>

      <style jsx>{`
        .pdf-viewer-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 16px;
          background-color: #f5f5f5;
          border-radius: 8px;
        }

        .pdf-viewer-header {
          width: 100%;
          text-align: center;
          margin-bottom: 16px;
        }

        .pdf-viewer-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }

        .pdf-viewer-loading,
        .pdf-viewer-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          min-height: 200px;
        }

        .pdf-document-container {
          display: flex;
          justify-content: center;
          max-width: 100%;
          overflow: auto;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          background-color: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        @media (max-width: 768px) {
          .pdf-viewer-container {
            padding: 8px;
          }
          
          .pdf-document-container {
            width: 100%;
            overflow-x: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default PDFViewer;
